import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { api, assetUrl } from "@/lib/api";
import {
  DEFAULT_IMAGES,
  fontStack,
  loadFont,
  normalizeConfig,
} from "@/lib/siteDefaults";

const SiteConfigContext = createContext(null);

function hexToRgba(hex, a) {
  try {
    let h = String(hex).replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const n = parseInt(h, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r},${g},${b},${a})`;
  } catch {
    return `rgba(16,16,16,${a})`;
  }
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const c = theme.colors || {};
  const dispStack = fontStack(theme.fonts?.display);
  const bodyStack = fontStack(theme.fonts?.body);
  const serifStack = fontStack(theme.fonts?.serif);
  loadFont(theme.fonts?.display);
  loadFont(theme.fonts?.body);
  loadFont(theme.fonts?.serif);

  const css = `:root{
    --om-paper:${c.paper};
    --om-ink:${c.ink};
    --om-char:${c.char};
    --om-noir:${c.noir};
    --om-accent:${c.accent || c.ink};
    --om-line:${hexToRgba(c.ink, 0.16)};
    --om-linew:${hexToRgba(c.paper, 0.18)};
    --om-font-display:${dispStack};
    --om-font-body:${bodyStack};
    --om-font-serif:${serifStack};
  }`;
  let tag = document.getElementById("om-theme");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "om-theme";
    document.head.appendChild(tag);
  }
  tag.textContent = css;
  document.documentElement.setAttribute(
    "data-om-space",
    theme.spacing?.section || "normal"
  );
}

function preloadHero(rawUrl) {
  if (typeof document === "undefined" || !rawUrl) return;
  const href = assetUrl(rawUrl);
  let link = document.getElementById("om-hero-preload");
  if (link && link.getAttribute("href") === href) return;
  if (!link) {
    link = document.createElement("link");
    link.id = "om-hero-preload";
    link.rel = "preload";
    link.as = "image";
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
  }
  link.href = href;
}

function getPreviewMode() {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("ompreview");
}

export function SiteConfigProvider({ children }) {
  const previewMode = getPreviewMode();
  const isPreview = previewMode === "draft" || previewMode === "live";
  const [config, setConfig] = useState(() => normalizeConfig(null));
  const [loading, setLoading] = useState(true);

  const applyConfig = useCallback((raw) => {
    const norm = normalizeConfig(raw);
    setConfig(norm);
    applyTheme(norm.theme);
    preloadHero(norm.images?.hero_portrait);
  }, []);

  // Initial fetch
  useEffect(() => {
    let alive = true;
    const endpoint = isPreview ? "/site/draft" : "/site";
    api
      .get(endpoint)
      .then((r) => {
        if (alive) applyConfig(r.data);
      })
      .catch(() => {
        // fallback: try published, else defaults
        if (isPreview) {
          api
            .get("/site")
            .then((r) => alive && applyConfig(r.data))
            .catch(() => alive && applyConfig(null));
        } else {
          if (alive) applyConfig(null);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [isPreview, applyConfig]);

  // Live preview via postMessage (iframe)
  useEffect(() => {
    if (previewMode !== "live") return;
    const onMsg = (e) => {
      if (e.data && e.data.type === "om-preview" && e.data.config) {
        applyConfig(e.data.config);
      }
    };
    window.addEventListener("message", onMsg);
    // tell parent we are ready
    try {
      window.parent?.postMessage({ type: "om-preview-ready" }, "*");
    } catch {}
    return () => window.removeEventListener("message", onMsg);
  }, [previewMode, applyConfig]);

  const value = useMemo(
    () => ({ config, loading, isPreview, previewMode, applyConfig }),
    [config, loading, isPreview, previewMode, applyConfig]
  );

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error("useSiteConfig must be used within SiteConfigProvider");
  return ctx;
}

export function useImages() {
  const { config } = useSiteConfig();
  return useMemo(() => {
    const merged = { ...DEFAULT_IMAGES, ...(config.images || {}) };
    const out = {};
    for (const k of Object.keys(merged)) {
      const v = merged[k];
      out[k] = Array.isArray(v) ? v.map(assetUrl) : assetUrl(v);
    }
    return out;
  }, [config.images]);
}

export function useSectionMeta(key) {
  const { config } = useSiteConfig();
  return config.sections?.items?.[key] || { visible: true, buttons: [] };
}
